import {
  createAuthorityWakeLoopServiceRuntimeId,
  isAgentExecutionReadyOrConnecting,
} from "@wakeloop/agent-runtime";
import { createAgentProfileBindingApi } from "@wakeloop/api-client";
import type {
  AgentControllerPermissionMode,
  EnsureAgentProfilePrimaryBindingResponse,
  GetAgentExecutionReadinessResponse,
  WakeLoopRequestContext,
} from "@wakeloop/schemas/types";
import { sleep } from "@wakeloop/utils";
import { resolveBaseUrl } from "../config/base-url";
import type { WakeLoopProfile } from "../config/wakeloop-profiles-api";
import { buildWakeLoopRequestContextFromProfile } from "../config/wakeloop-request-context";
import {
  type RuntimeAgentControllerReportSubmission,
  submitProfileRuntimeAgentControllerReport,
} from "../local-service/reporting/runtime-agent-controller-reporting-submission";
import { createCliApiClientFoundation } from "../space/api-client-foundation";
import { assessActionParticipationCandidate } from "./action-participation-planner";
import { resolveCurrentReplyReadinessSnapshot } from "./current-reply-readiness-snapshot";
import { resolveServiceParticipationReadiness } from "./service-participation-readiness";

const EXECUTION_READINESS_AFTER_BINDING_MAX_ATTEMPTS = 8;
const EXECUTION_READINESS_AFTER_BINDING_RETRY_DELAY_MS = 500;

export type AgentProfileRuntimeBindingEnsureResult = {
  existingBinding?: AgentProfileRuntimeBindingExistingBinding;
  source: "agent_profile_binding_api";
  status: "accepted" | "rejected" | "skipped" | "unavailable";
  outcome?: "created" | "reused" | "reconnected";
  reason?:
    | "binding_rejected"
    | "binding_write_failed"
    | "local_execution_evidence_unavailable"
    | "profile_not_agent"
    | "runtime_reporting_unavailable";
  reasonCodes: string[];
  routable: boolean;
};

export type AgentProfileRuntimeBindingExistingBinding = {
  agentControllerConversationId?: string | null;
  agentControllerRef: string;
  wakeLoopServiceRuntimeId: string;
  agentProfileId?: string;
  bindingId?: string;
  bindingState?: "enabled_primary" | "disabled_historical";
  createdAtMs?: number;
  createdByProfileId?: string | null;
  disabledAtMs?: number | null;
  owner?: {
    id: string;
    kind: "owner_account";
  };
  reasonCode?: string | null;
  updatedAtMs?: number;
  agentControllerPermissionMode?: string | null;
  modelId?: string | null;
  reasoningLevel?: string | null;
};

export async function ensureAgentProfileRuntimeBinding(input: {
  agentControllerConversationId?: string | null;
  agentControllerPermissionMode?: AgentControllerPermissionMode | null;
  confirmLocalExecutionEvidence?: boolean;
  modelId?: string | null;
  profile: WakeLoopProfile;
  reasoningLevel?: string | null;
  runtimeReportSubmission: RuntimeAgentControllerReportSubmission;
  writeMode?: "ensure" | "reconnect";
}): Promise<AgentProfileRuntimeBindingEnsureResult> {
  if (input.profile.profileKind !== "agent") {
    return {
      reason: "profile_not_agent",
      reasonCodes: ["agent_profile_binding.profile_not_agent"],
      routable: false,
      source: "agent_profile_binding_api",
      status: "skipped",
    };
  }

  const bindingInput = tryResolveBindingInput(input.runtimeReportSubmission);
  if (!bindingInput) {
    return {
      reason: "runtime_reporting_unavailable",
      reasonCodes: ["agent_profile_binding.runtime_reporting_unavailable"],
      routable: false,
      source: "agent_profile_binding_api",
      status: "unavailable",
    };
  }

  try {
    const gatewayUrl = await resolveBaseUrl();
    const api = createAgentProfileBindingApi(
      createCliApiClientFoundation(gatewayUrl)
    );
    const requestContext = buildWakeLoopRequestContextFromProfile({
      wakeLoopProfile: input.profile,
    });
    const agentControllerConversationId = normalizeOptionalText(
      input.agentControllerConversationId
    );
    const modelId = normalizeOptionalText(input.modelId);
    const reasoningLevel = modelId
      ? normalizeOptionalText(input.reasoningLevel)
      : null;
    const response = await api.ensurePrimaryBinding({
      agentProfileId: input.profile.wakeLoopProfileId,
      ...(input.agentControllerPermissionMode
        ? { agentControllerPermissionMode: input.agentControllerPermissionMode }
        : {}),
      ...(agentControllerConversationId
        ? { agentControllerConversationId }
        : {}),
      agentControllerRef: bindingInput.agentControllerRef,
      deviceId: bindingInput.deviceId,
      lane: bindingInput.lane,
      requestContext,
      wakeLoopServiceRuntimeId: bindingInput.wakeLoopServiceRuntimeId,
      ...(modelId ? { modelId } : {}),
      ...(reasoningLevel ? { reasoningLevel } : {}),
      ...(input.writeMode === undefined ? {} : { writeMode: input.writeMode }),
    });

    if (response.status === "accepted") {
      if (input.confirmLocalExecutionEvidence === false) {
        return {
          outcome: response.outcome,
          reasonCodes: uniqueReasonCodes(response.reasonCodes),
          routable: response.routable,
          source: "agent_profile_binding_api",
          status: "accepted",
        };
      }

      const localExecutionEvidence =
        await confirmLocalExecutionEvidenceAfterBinding({
          agentProfileId: input.profile.wakeLoopProfileId,
          api,
          requestContext,
        });
      if (localExecutionEvidence.status !== "accepted") {
        return {
          reason: "local_execution_evidence_unavailable",
          reasonCodes: uniqueReasonCodes(localExecutionEvidence.reasonCodes),
          routable: false,
          source: "agent_profile_binding_api",
          status: "unavailable",
        };
      }

      return {
        outcome: response.outcome,
        reasonCodes: uniqueReasonCodes([
          ...response.reasonCodes,
          ...localExecutionEvidence.reasonCodes,
        ]),
        routable: response.routable,
        source: "agent_profile_binding_api",
        status: "accepted",
      };
    }

    const existingBinding = resolveExistingBindingFromCommandResponse(response);
    return {
      ...(existingBinding ? { existingBinding } : {}),
      reason: "binding_rejected",
      reasonCodes: response.reasonCodes,
      routable: false,
      source: "agent_profile_binding_api",
      status: "rejected",
    };
  } catch (error) {
    console.warn("[wakeloop.agent_profile_binding.ensure_failed]", {
      error: toErrorMessage(error),
      event: "cli.agent_profile_binding.ensure_failed",
      profileId: input.profile.wakeLoopProfileId,
    });
    return {
      reason: "binding_write_failed",
      reasonCodes: ["agent_profile_binding.write_failed"],
      routable: false,
      source: "agent_profile_binding_api",
      status: "unavailable",
    };
  }
}

type AgentProfileBindingApi = ReturnType<typeof createAgentProfileBindingApi>;

function resolveExistingBindingFromCommandResponse(
  response: EnsureAgentProfilePrimaryBindingResponse
): AgentProfileRuntimeBindingExistingBinding | null {
  const executionTarget = response.binding?.executionTarget ?? null;
  const agentControllerRef = normalizeOptionalText(
    executionTarget?.agentControllerRef
  );
  const wakeLoopServiceRuntimeId = normalizeOptionalText(
    executionTarget?.wakeLoopServiceRuntimeId.value
  );
  if (!(agentControllerRef && wakeLoopServiceRuntimeId)) {
    return null;
  }
  return {
    agentControllerRef,
    wakeLoopServiceRuntimeId,
    ...(executionTarget?.agentControllerConversationId
      ? {
          agentControllerConversationId:
            executionTarget.agentControllerConversationId,
        }
      : {}),
  };
}

async function confirmLocalExecutionEvidenceAfterBinding(input: {
  agentProfileId: string;
  api: AgentProfileBindingApi;
  requestContext: WakeLoopRequestContext;
}): Promise<
  | {
      reasonCodes: readonly string[];
      status: "accepted";
    }
  | {
      reasonCodes: readonly string[];
      status: "unavailable";
    }
> {
  const notification = await input.api.notifyRouteCatalogChanged({
    agentProfileId: input.agentProfileId,
    reason: "agent_profile_binding_changed",
    requestContext: input.requestContext,
  });
  if (!notification.ok || notification.notifiedSessions < 1) {
    return {
      reasonCodes: ["agent_profile_binding.local_service_not_connected"],
      status: "unavailable",
    };
  }

  const readiness = await waitForExecutionReadinessAfterBinding(input);
  if (!readiness) {
    return {
      reasonCodes: ["agent_profile_binding.execution_readiness_pending"],
      status: "unavailable",
    };
  }
  if (isAgentExecutionReadyOrConnecting(readiness.agentExecutionReadiness)) {
    return {
      reasonCodes: readiness.agentExecutionReadiness.reasonCodes,
      status: "accepted",
    };
  }
  return {
    reasonCodes: readiness.agentExecutionReadiness.reasonCodes,
    status: "unavailable",
  };
}

async function waitForExecutionReadinessAfterBinding(input: {
  agentProfileId: string;
  api: AgentProfileBindingApi;
  requestContext: WakeLoopRequestContext;
}): Promise<GetAgentExecutionReadinessResponse | null> {
  let latest: GetAgentExecutionReadinessResponse | null = null;
  for (
    let attempt = 1;
    attempt <= EXECUTION_READINESS_AFTER_BINDING_MAX_ATTEMPTS;
    attempt++
  ) {
    latest = await input.api.getExecutionReadiness({
      agentProfileId: input.agentProfileId,
      requestContext: input.requestContext,
    });
    if (isAgentExecutionReadyOrConnecting(latest.agentExecutionReadiness)) {
      return latest;
    }
    await sleep(EXECUTION_READINESS_AFTER_BINDING_RETRY_DELAY_MS);
  }
  return latest;
}

export async function syncAgentProfileRuntimeBindingFromCurrentReadiness(input: {
  agentControllerConversationId?: string | null;
  profile: WakeLoopProfile;
}): Promise<{
  agentProfileBindingEnsure: AgentProfileRuntimeBindingEnsureResult;
  runtimeReportSubmission: RuntimeAgentControllerReportSubmission;
}> {
  if (input.profile.profileKind !== "agent") {
    const runtimeReportSubmission: RuntimeAgentControllerReportSubmission = {
      reason: "agent_controller_ref_unavailable",
      reasonCodes: ["runtime.reporting.profile_not_agent"],
      source: "runtime_reporting_api",
      status: "unavailable",
    };
    return {
      agentProfileBindingEnsure: await ensureAgentProfileRuntimeBinding({
        agentControllerConversationId: input.agentControllerConversationId,
        profile: input.profile,
        runtimeReportSubmission,
      }),
      runtimeReportSubmission,
    };
  }

  const snapshot = await resolveCurrentReplyReadinessSnapshot({
    checkGatewayChain: true,
    includeDaemonRouteObservation: true,
    openClawDiagnosticsMode: "repair",
    ownerUserId: input.profile.ownerUserId,
    profile: input.profile,
  });
  const serviceReadiness = resolveServiceParticipationReadiness({
    deviceReplyReadiness: snapshot.deviceReplyReadiness,
    inventory: snapshot.inventory,
    runtimeStatus: snapshot.displayRuntimeStatus ?? snapshot.runtimeStatus,
  });
  const assessment = assessActionParticipationCandidate({
    profileId: input.profile.wakeLoopProfileId,
    profileName: input.profile.profileName,
    profileKind: input.profile.profileKind,
    localParticipationReadiness: serviceReadiness.serviceReadiness,
    localParticipationReasonCodes: snapshot.deviceReplyReadiness.reasonCodes,
  });
  const runtimeReportSubmission =
    await submitProfileRuntimeAgentControllerReport({
      localParticipationState: assessment.localParticipationState,
      profile: input.profile,
      reasonCodes: assessment.reasonCodes,
    });
  return {
    agentProfileBindingEnsure: await ensureAgentProfileRuntimeBinding({
      agentControllerConversationId: input.agentControllerConversationId,
      profile: input.profile,
      runtimeReportSubmission,
    }),
    runtimeReportSubmission,
  };
}

function tryResolveBindingInput(
  submission: RuntimeAgentControllerReportSubmission
): {
  agentControllerRef: string;
  deviceId: string;
  lane: string;
  wakeLoopServiceRuntimeId: {
    source: "runtime_registry_authority";
    value: string;
  };
} | null {
  if (
    submission.status !== "submitted" ||
    submission.agentControllerReportStatus !== "accepted" ||
    !submission.agentControllerRef ||
    !submission.deviceId ||
    !submission.lane ||
    !submission.wakeLoopServiceRuntimeId
  ) {
    return null;
  }

  let wakeLoopServiceRuntimeId: {
    source: "runtime_registry_authority";
    value: string;
  };
  try {
    wakeLoopServiceRuntimeId = createAuthorityWakeLoopServiceRuntimeId({
      wakeLoopServiceRuntimeId: submission.wakeLoopServiceRuntimeId,
      source: "runtime_registry_authority",
    });
  } catch {
    return null;
  }

  return {
    agentControllerRef: submission.agentControllerRef,
    deviceId: submission.deviceId,
    lane: submission.lane,
    wakeLoopServiceRuntimeId,
  };
}

function uniqueReasonCodes(reasonCodes: readonly string[]): string[] {
  return [...new Set(reasonCodes)];
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message.trim();
    return message.length > 0 ? message : "unknown error";
  }
  const message = String(error ?? "").trim();
  return message.length > 0 ? message : "unknown error";
}

function normalizeOptionalText(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}
